/**
 * Billing API client. Routes to the mock adapter until `/api/v1/invoices`
 * and `/api/v1/payments` land on the backend.
 */
import { apiClient } from "./client";
import { MOCKS_ENABLED } from "./mock/registry";
import { mockBilling } from "./mock/billing";
import type {
  AllocationInputRow,
  BillingMedicineOption,
  BillingPatientOption,
  BillingServiceOption,
  BillingTotalsByStatus,
  InvoiceCreateRequest,
  InvoiceDetail,
  InvoiceListFilter,
  InvoiceSummary,
  Payment,
  PaymentCreateRequest,
  PaymentListFilter,
  PaymentMethod,
} from "../types/billing";
import type { PageResponse } from "../types/patient";

const realClient = {
  listServices(): Promise<BillingServiceOption[]> {
    return apiClient
      .get<
        { id: string; name: string; durationMinutes: number; priceVnd: number }[]
      >("/v1/services")
      .then((r) =>
        r.data.map((s) => ({
          id: s.id,
          name: s.name,
          unit: null,
          priceVnd: s.priceVnd ?? 0,
          vatCategory: "KCT" as const,
        }))
      );
  },
  listMedicines(): Promise<BillingMedicineOption[]> {
    return apiClient
      .get<{ content?: { id: string; name: string; unit: string }[] }>(
        "/v1/medicines",
        { params: { size: 500 } }
      )
      .then((r) =>
        (r.data.content ?? []).map((m) => ({
          id: m.id,
          name: m.name,
          unit: m.unit ?? "",
          sellPrice: 0,
          vatCategory: "KCT" as const,
        }))
      );
  },
  searchPatients(q: string): Promise<BillingPatientOption[]> {
    if (!q || q.trim().length < 1) return Promise.resolve([]);
    return apiClient
      .get<
        {
          id: string;
          fullName: string;
          patientCode: string | null;
          phoneE164: string | null;
          dateOfBirth: string | null;
        }[]
      >("/v1/patients/lookup", { params: { q } })
      .then((r) =>
        r.data.map((p) => ({
          id: p.id,
          code: p.patientCode,
          name: p.fullName,
          phone: p.phoneE164,
          dateOfBirth: p.dateOfBirth,
        }))
      );
  },
  getPatient(id: string): Promise<BillingPatientOption | null> {
    return apiClient
      .get<{
        id: string;
        fullName: string;
        patientCode: string | null;
        phoneE164: string | null;
        dateOfBirth: string | null;
      }>(`/v1/patients/${id}`)
      .then((r) => ({
        id: r.data.id,
        code: r.data.patientCode,
        name: r.data.fullName,
        phone: r.data.phoneE164,
        dateOfBirth: r.data.dateOfBirth,
      }))
      .catch(() => null);
  },
  list(
    filter: InvoiceListFilter | undefined,
    pageable: { page?: number; size?: number; sort?: string }
  ): Promise<PageResponse<InvoiceSummary>> {
    const params: Record<string, string | number> = {};
    if (filter?.q) params.q = filter.q;
    if (filter?.status) params.status = filter.status;
    if (filter?.patientId) params.patientId = filter.patientId;
    if (filter?.fromDate) params.fromDate = filter.fromDate;
    if (filter?.toDate) params.toDate = filter.toDate;
    if (pageable.page != null) params.page = pageable.page;
    if (pageable.size != null) params.size = pageable.size;
    return apiClient
      .get<PageResponse<InvoiceSummary>>("/v1/invoices", { params })
      .then((r) => r.data);
  },
  totalsByStatus(
    filter: InvoiceListFilter | undefined
  ): Promise<BillingTotalsByStatus[]> {
    // CÙNG bộ lọc với list() (#19845). Trước đây tham số bị bỏ (`_filter`) nên ô KPI "(kỳ lọc)"
    // luôn là tổng mọi thời điểm trong khi bảng bên dưới đã lọc.
    const params: Record<string, string> = {};
    if (filter?.q) params.q = filter.q;
    if (filter?.status) params.status = filter.status;
    if (filter?.patientId) params.patientId = filter.patientId;
    if (filter?.fromDate) params.fromDate = filter.fromDate;
    if (filter?.toDate) params.toDate = filter.toDate;
    return apiClient
      .get<BillingTotalsByStatus[]>("/v1/invoices/totals", { params })
      .then((r) => r.data);
  },
  get(id: string): Promise<InvoiceDetail> {
    return apiClient
      .get<InvoiceDetail>(`/v1/invoices/${id}`)
      .then((r) => r.data);
  },
  create(req: InvoiceCreateRequest): Promise<InvoiceDetail> {
    return apiClient
      .post<InvoiceDetail>("/v1/invoices", req)
      .then((r) => r.data);
  },
  /**
   * Build (or return the existing) UNPAID invoice from an encounter's thuốc +
   * chỉ định. Frozen contract: returns the invoice (at least `{ id }`).
   */
  createInvoiceFromEncounter(encounterId: string): Promise<{ id: string }> {
    return apiClient
      .post<{ id: string }>(`/v1/invoices/from-encounter/${encounterId}`)
      .then((r) => r.data);
  },
  /**
   * Tự tạo hoá đơn khi ký lần khám (#18527): tách theo nguồn — 1 hoá đơn thuốc +
   * 1 hoá đơn dịch vụ, chỉ khi có khoản > 0đ. Phí khám không nằm ở đây. Idempotent
   * theo (lần khám, loại). Trả về danh sách hoá đơn hiện hành của lần khám (0–2).
   */
  autoInvoicesFromEncounter(encounterId: string): Promise<{ id: string }[]> {
    return apiClient
      .post<{ id: string }[]>(`/v1/invoices/auto-from-encounter/${encounterId}`)
      .then((r) => r.data);
  },
  /** Set exam mode + BHYT-covered amount (only while OPEN + unpaid). */
  setBhyt(
    id: string,
    req: { paymentType: "SERVICE" | "BHYT"; coveredVnd: number },
  ): Promise<InvoiceDetail> {
    return apiClient.patch<InvoiceDetail>(`/v1/invoices/${id}/bhyt`, req).then((r) => r.data);
  },
  /**
   * Replaces every allocation on one line (#18836). Replace, not patch: a line's allocations only
   * mean anything as a set that sums to the line total, so partial edits have no useful meaning.
   * An empty array clears them.
   */
  replaceAllocations(
    invoiceId: string,
    itemId: string,
    allocations: AllocationInputRow[],
  ): Promise<InvoiceDetail> {
    return apiClient
      .put<InvoiceDetail>(`/v1/invoices/${invoiceId}/items/${itemId}/allocations`, { allocations })
      .then((r) => r.data);
  },
  post(id: string): Promise<InvoiceDetail> {
    return apiClient
      .post<InvoiceDetail>(`/v1/invoices/${id}/post`)
      .then((r) => r.data);
  },
  void(id: string, reason: string): Promise<InvoiceDetail> {
    return apiClient
      .post<InvoiceDetail>(`/v1/invoices/${id}/void`, { reason })
      .then((r) => r.data);
  },
  remove(id: string): Promise<void> {
    return apiClient.delete(`/v1/invoices/${id}/permanent`).then(() => undefined);
  },
  /**
   * Ghi nhận một khoản đã thu.
   *
   * `idempotencyKey` không phải tuỳ chọn cho vui: server đã đọc header này từ lâu
   * (`PaymentController`), nhưng web chưa bao giờ gửi. Bấm hai lần, hay một lần gửi lại ở tầng
   * mạng, là GHI TIỀN HAI LẦN — với khoản thu một phần thì không có gì chặn, vì mỗi lần vẫn nhỏ
   * hơn phần còn phải trả. Nút có `disabled` khi đang gửi, nhưng đó là phòng tuyến của giao diện,
   * không phải của tiền.
   */
  recordPayment(
    req: PaymentCreateRequest,
    idempotencyKey?: string,
  ): Promise<InvoiceDetail> {
    return apiClient
      .post<InvoiceDetail>("/v1/payments", req, {
        headers: idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {},
      })
      .then((r) => r.data);
  },
  listPayments(
    filter: PaymentListFilter | undefined,
    pageable: { page?: number; size?: number }
  ): Promise<PageResponse<Payment>> {
    const params: Record<string, string | number> = {};
    if (filter?.q) params.q = filter.q;
    if (filter?.method) params.method = filter.method;
    if (filter?.fromDate) params.fromDate = filter.fromDate;
    if (filter?.toDate) params.toDate = filter.toDate;
    if (pageable.page != null) params.page = pageable.page;
    if (pageable.size != null) params.size = pageable.size;
    return apiClient
      .get<PageResponse<Payment>>("/v1/payments", { params })
      .then((r) => r.data);
  },
  paymentTotalsByMethod(
    filter: PaymentListFilter | undefined
  ): Promise<{ method: PaymentMethod; count: number; amount: number }[]> {
    // CÙNG bộ lọc với listPayments() (#19845).
    const params: Record<string, string> = {};
    if (filter?.q) params.q = filter.q;
    if (filter?.method) params.method = filter.method;
    if (filter?.fromDate) params.fromDate = filter.fromDate;
    if (filter?.toDate) params.toDate = filter.toDate;
    return apiClient
      .get<{ method: PaymentMethod; count: number; amount: number }[]>(
        "/v1/payments/totals",
        { params }
      )
      .then((r) => r.data);
  },
};

export const billingApi = MOCKS_ENABLED ? mockBilling : (realClient as unknown as typeof mockBilling);

export const billingQueryKeys = {
  all: ["billing"] as const,
  invoiceList: (filter: InvoiceListFilter | undefined, page: number) =>
    ["billing", "invoices", "list", filter, page] as const,
  invoiceTotals: (filter: InvoiceListFilter | undefined) =>
    ["billing", "invoices", "totals", filter] as const,
  invoiceDetail: (id: string) => ["billing", "invoices", "detail", id] as const,
  paymentList: (filter: PaymentListFilter | undefined, page: number) =>
    ["billing", "payments", "list", filter, page] as const,
  paymentTotals: (filter: PaymentListFilter | undefined) =>
    ["billing", "payments", "totals", filter] as const,
  services: () => ["billing", "services"] as const,
  medicines: () => ["billing", "medicines"] as const,
  patientSearch: (q: string) => ["billing", "patients", "search", q] as const,
};
